import React, {useState} from "react";
import {Box, Divider, IconButton, Menu, MenuItem, Tooltip, Typography} from "@mui/material";
import {AccountIcon, SitesIcon} from "./icons";
import parkourLogo from "../assets/ParkourLogo.svg";
import {APP_NAME, BRAND, SITES} from "../util/portal";
import {getAccount, getUser} from "../util/SessionContext";

// The portal title bar, built to match the staff applications' NavBar
// (cres-t{1,2,5}/src/components/Navigation/NavBar.tsx): a white bar under a thin grey rule, the
// Parkour-AI mark and wordmark at the left, then the application name, separated by pipe rules.
//
// It carries two controls and no more -- the site selector and the account button. The staff bar's
// other furniture (notifications, assistant, persona switching, the applications drawer) has no
// counterpart here: an external contact has one account at a time and nothing to switch between.

const WHITE = "#FFFFFF";
const BLUE3 = "#1498D8";
const BLUE4 = "#0575AD";
const GREY2 = "#EDEFF3";
const GREY4 = "#BAC3CE";

// The staff NavMenuButton, in sx form: a slate glyph on white that turns blue on hover.
const navButtonSx = {
    backgroundColor: WHITE,
    color: "#525968",
    borderRadius: "5px",
    height: 30,
    minWidth: 30,
    "&:hover": {backgroundColor: WHITE, color: BLUE3},
    "&:active": {backgroundColor: WHITE, color: "#525968"},
};

// Type sizes, weights and paddings below are lifted verbatim from the staff bar rather than
// approximated -- the two sit side by side in a user's browser and a 2px drift reads as a different
// product. 24px throughout; the wordmark is the only thing at weight 500.
const pipeSx = {fontSize: 24, fontWeight: 400, color: GREY4, letterSpacing: "normal"};

export default function NavBar({signedIn, onSignIn, onSignOut}: {
    signedIn: boolean;
    onSignIn: () => void;
    onSignOut: () => void;
}) {
    const [sitesAnchor, setSitesAnchor] = useState<HTMLElement | null>(null);
    const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);

    // Read at render rather than held in state: sign-in and sign-out both re-render this component
    // through `signedIn`, and sessionStorage is the single source of truth for both.
    const account = signedIn ? getAccount() : null;
    const user = signedIn ? getUser() : null;

    return (
        <Box component="header" sx={{
            position: "relative", height: "50px", flexShrink: 0, boxSizing: "content-box",
            display: "flex", flexDirection: "row", justifyContent: "flex-start",
            paddingLeft: "20px",
            borderBottomWidth: "1px", borderBottomStyle: "solid", borderBottomColor: GREY2,
            backgroundColor: WHITE,
        }}>
            {/* Absolutely positioned and 58.5x50 in a 50px bar, exactly as the staff .ParkourLogo
                rule has it: the mark fills the bar's full height and the wordmark clears it on a
                57px margin rather than by sitting after it in the flow. */}
            <Box sx={{paddingTop: "0px", paddingRight: "2px", marginLeft: "-5px"}}>
                <Box component="img" src={parkourLogo} alt=""
                     sx={{position: "absolute", width: "58.5px", height: "50px"}}/>
            </Box>

            <Box sx={{paddingTop: "5px", marginLeft: "57px", marginRight: "4px"}}>
                <Typography sx={pipeSx}>|</Typography>
            </Box>

            <Box sx={{paddingTop: "7px"}}>
                <Typography component="h1" sx={{fontSize: 24, fontWeight: 500, color: BLUE4, whiteSpace: "nowrap", letterSpacing: "normal"}}>
                    &nbsp;&nbsp;{BRAND}
                </Typography>
            </Box>

            <Box sx={{paddingTop: "5px", marginLeft: "15px", marginRight: "4px"}}>
                <Typography sx={pipeSx}>|</Typography>
            </Box>

            <Box sx={{paddingTop: "7px"}}>
                <Typography sx={{fontSize: 24, fontWeight: 400, color: BLUE3, whiteSpace: "nowrap", letterSpacing: "normal"}}>
                    &nbsp;&nbsp;{APP_NAME}
                </Typography>
            </Box>

            <Box sx={{flexGrow: 1}}/>

            {/* The staff bar's right-hand rail: a 4px drop, then one 45px slot per control. */}
            <Box sx={{display: "flex", flexDirection: "row", justifyContent: "flex-end", paddingTop: "4px"}}>
                <Box sx={{width: "45px", paddingTop: "2px"}}>
                    <Tooltip title="Sites Portfolio">
                        <IconButton sx={navButtonSx} aria-label="Sites"
                                    onClick={e => setSitesAnchor(e.currentTarget)}>
                            <SitesIcon/>
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={{width: "45px", paddingTop: "2px"}}>
                    {/* One button, two jobs, as in the staff bar: it opens sign-in while there is no
                        session and the account menu once there is. */}
                    <Tooltip title={signedIn ? (user?.Name ?? "Account") : "Sign in"}>
                        <IconButton sx={navButtonSx} aria-label={signedIn ? "Account" : "Sign in"}
                                    onClick={e => signedIn ? setAccountAnchor(e.currentTarget) : onSignIn()}>
                            <AccountIcon/>
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Menu anchorEl={sitesAnchor} open={Boolean(sitesAnchor)} onClose={() => setSitesAnchor(null)}
                  anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                  transformOrigin={{vertical: "top", horizontal: "right"}}
                  slotProps={{paper: {sx: {mt: 1, minWidth: 300}}}}>
                {SITES.map(site => (
                    <MenuItem key={site.url} disabled={site.current}
                              onClick={() => { setSitesAnchor(null); window.location.assign(site.url); }}
                              sx={{display: "block", py: 1.25}}>
                        <Typography sx={{fontSize: 15, fontWeight: 500, color: "#2B2B2B"}}>
                            {site.name}{site.current ? " (you are here)" : ""}
                        </Typography>
                        <Typography sx={{fontSize: 13, color: "#717171", whiteSpace: "normal"}}>
                            {site.description}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>

            {signedIn && (
                <Menu anchorEl={accountAnchor} open={Boolean(accountAnchor)} onClose={() => setAccountAnchor(null)}
                      anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                      transformOrigin={{vertical: "top", horizontal: "right"}}
                      slotProps={{paper: {sx: {mt: 1, minWidth: 240}}}}>
                    <Box sx={{px: 2, py: 1}}>
                        <Typography sx={{fontSize: 14, fontWeight: 500, color: "#2B2B2B"}}>{user?.Name}</Typography>
                        <Typography sx={{fontSize: 13, color: "#717171"}}>{user?.Email}</Typography>
                        {account && (
                            <Typography sx={{fontSize: 13, color: "#717171", mt: 0.5}}>{account.AccountName}</Typography>
                        )}
                    </Box>
                    <Divider/>
                    <MenuItem onClick={() => { setAccountAnchor(null); onSignOut(); }} sx={{fontSize: 14}}>
                        Sign out
                    </MenuItem>
                </Menu>
            )}
        </Box>
    );
}
